// Structural names for the things in a run: one per trace event, one per
// scheduler step.
//
// The playground lets a user drag a trace event past another to explore a
// different interleaving, which re-runs the program from the beginning — so a
// reordering has to be expressed in names that survive that re-run. Row indices
// and thread/txn/version numbers don't (they are assigned by the run being
// replaced), so both names here are hashes over *causal history* instead. See
// docs/design/trace-reordering.md.
//
//   event id — hash(payload ‖ ids of the events that caused this one). Names a
//     trace row. It changes exactly when the event genuinely changes: drag a read
//     above the write it read from and the read now sees an older value, so its
//     payload and its reads-from edge both differ, so its id differs. That is the
//     point — it tells the view "this row is new", and it is why an event id is
//     the wrong thing to file a reordering under.
//
//   step id — hash(id of the thread's most recent event ‖ steps taken since).
//     Names a *scheduling decision* by the thread's history entering it, which is
//     independent of the reordering that changes the event coming out of it. This
//     is what a stored reordering refers to.
//
// The causal edges recorded here are the full happens-before relation (program
// order, thread creation, reads-from, validation, retry wakes, lock handoffs).
// Only a sub-relation of it constrains reordering — a step may not move above the
// step whose `enqueue` made it runnable — and that one is recorded by the
// scheduler on the ready entry (`StepRecord.enabledBy`), not derived from here.

import type { TraceEvent } from "./trace.js";

export type EventId = string;
export type StepId = string;

/** One scheduler step: a single `gen.next()` on one thread — everything it does
 *  between two consecutive pauses, which is zero or more events followed by the
 *  effect it yielded. A step that emitted no events is an *empty step*; it is
 *  still a step, and still occupies a position. */
export interface StepRecord {
  /** The thread that ran. */
  thread: number;
  /** Virtual time it ran at. */
  time: number;
  /** Stable name for this scheduling decision (see the module comment). */
  id: StepId;
  /** Index of the step during whose handling this one was enqueued — the only
   *  relation that constrains reordering. -1 for main's initial enqueue. */
  enabledBy: number;
  /** Trace events emitted during this step, as a `[first, end)` range. Usually
   *  the acting thread's own, but a step can emit another thread's event (a
   *  lock release wakes a parked acquirer). Empty when the step emitted none. */
  firstEvent: number;
  endEvent: number;
  /** The threads the scheduler could have run here: everything ready at the
   *  minimum ready time, in the order the tie-break prefers, so `choices[0]` is
   *  what the heuristic takes and `thread` is what actually ran (they differ only
   *  under a `RunOptions.schedule` override).
   *
   *  One entry means the step was forced by the program, not chosen. More than
   *  one is a fork in the space of executions — the branching this run happened
   *  to resolve one way, and the only place a different schedule can exist. */
  choices: number[];
}

/** Everything `RunOptions.causality` adds to a run, alongside the trace. */
export interface Causality {
  /** One id per trace event, index-aligned with `trace.events`. */
  eventIds: EventId[];
  /** Immediate causal predecessors of each event, as event indices. */
  deps: number[][];
  /** The step each event was emitted during, index-aligned with the events. */
  eventStep: number[];
  /** One record per scheduler step, in execution order. */
  steps: StepRecord[];
}

// ---------------------------------------------------------------------------
// Hashing
// ---------------------------------------------------------------------------

/** A 64-bit-ish hash of a list of strings, as 16 hex chars. Two independent
 *  32-bit mixes concatenated — enough to name events in a trace (collisions are
 *  a cosmetic mis-highlight, never a correctness bug: every use re-checks the
 *  answer against the run it came from). */
export function hashParts(parts: readonly string[]): string {
  let a = 0x811c9dc5;
  let b = 0x9e3779b9;
  for (const part of parts) {
    for (let i = 0; i < part.length; i++) {
      const c = part.charCodeAt(i);
      a = Math.imul(a ^ c, 0x01000193) >>> 0;
      b = Math.imul((b + c) >>> 0, 0x85ebca6b) >>> 0;
      b = (b ^ (b >>> 13)) >>> 0;
    }
    // A separator, so ["ab","c"] and ["a","bc"] don't collide.
    a = Math.imul(a ^ 0x2f, 0x01000193) >>> 0;
    b = Math.imul(b ^ 0x2f, 0xc2b2ae35) >>> 0;
  }
  return a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0");
}

/**
 * The part of an event that is the event *itself*, with every sequential
 * counter stripped out: thread ids, txn ids, store versions, virtual time, and
 * a spawn's child id are all allocation-order artifacts that a reordering can
 * shift without anything meaningful having changed. Identity reaches the hash
 * through the causal chain instead — a thread *is* its spawn event's id, and a
 * read's version *is* its reads-from edge.
 */
export function eventPayload(ev: TraceEvent): string {
  const head = `${ev.type}@${ev.line}`;
  switch (ev.type) {
    case "spawn":
    case "thread-start":
    case "thread-end":
      return `${head}|${ev.fn}`;
    case "sleep":
      return `${head}|${ev.duration}`; // `until` is time-relative
    case "txn-begin":
      return head;
    case "txn-commit":
      return `${head}|${ev.reads}r|${ev.writes}w`;
    case "txn-abort":
      return `${head}|${ev.reason}|${ev.attempt}`;
    case "txn-retry":
      return `${head}|${ev.reads}r`;
    case "orelse-retry":
      return head;
    case "lock-acquire":
    case "lock-wait":
    case "lock-release":
      return `${head}|${ev.addrs.join(",")}`;
    case "read":
    case "write":
      return `${head}|${ev.addr}|${ev.value}`;
    case "print":
      return `${head}|${ev.message}`;
    case "assert":
      return `${head}|${ev.passed}|${ev.message}`;
  }
}

/** The thread an event belongs to for program-order purposes (a `spawn` is the
 *  parent's event; everything else names its thread directly). */
export function eventThread(ev: TraceEvent): number {
  return ev.type === "spawn" ? ev.parent : ev.thread;
}

// ---------------------------------------------------------------------------
// Tracker
// ---------------------------------------------------------------------------

/** Extra causal predecessors the interpreter knows and the event doesn't carry:
 *  the release a lock handoff came from, the commit that invalidated a read set. */
export interface ExtraCauses {
  extra?: number[];
}

/**
 * Accumulates the causality record during a run. Instantiated only when
 * `RunOptions.causality` is on; the interpreter holds `null` otherwise, so a
 * plain run pays nothing.
 */
export class CausalityTracker {
  readonly eventIds: EventId[] = [];
  readonly deps: number[][] = [];
  readonly eventStep: number[] = [];
  readonly steps: StepRecord[] = [];

  /** Index of each thread's most recent event (the program-order edge). */
  private lastEvent = new Map<number, number>();
  /** Index of the `spawn` event that created each thread. */
  private spawnOf = new Map<number, number>();
  /** Each thread's chain head: the id of its most recent event, or — before it
   *  has emitted any — the id of the spawn that created it. */
  private chainHead = new Map<number, EventId>();
  /** Steps a thread has taken since its last event, so consecutive empty steps
   *  still get distinct step ids. */
  private sinceEvent = new Map<number, number>();
  /** `addr@version` → the event that published that version. Serves both the
   *  reads-from edge and the validation edge. */
  private author = new Map<string, number>();
  /** Threads woken out of a `retry` park, awaiting the commit event that woke
   *  them (`wakeRetryParked` runs just before that commit is emitted). */
  private pendingWakes: number[] = [];
  /** Resolved wakes: thread → the commit event that woke it, consumed by that
   *  thread's next event (its re-attempt's `txn-begin`). */
  private wokenBy = new Map<number, number>();

  private currentStep = -1;

  constructor() {
    this.chainHead.set(0, hashParts(["thread", "main"]));
    this.sinceEvent.set(0, 0);
  }

  result(): Causality {
    return {
      eventIds: this.eventIds,
      deps: this.deps,
      eventStep: this.eventStep,
      steps: this.steps,
    };
  }

  /** Open a step. Its id is fixed by the acting thread's history *entering* it,
   *  so it is decided here, before the thread runs. */
  beginStep(
    thread: number,
    time: number,
    enabledBy: number,
    eventCount: number,
    choices: number[]
  ): void {
    const head = this.chainHead.get(thread) ?? hashParts(["thread", String(thread)]);
    const since = this.sinceEvent.get(thread) ?? 0;
    this.currentStep = this.steps.length;
    this.steps.push({
      thread,
      time,
      id: hashParts([head, String(since)]),
      enabledBy,
      firstEvent: eventCount,
      endEvent: eventCount,
      choices,
    });
  }

  /** Close the step opened by `beginStep`. A thread whose chain head did not
   *  move this step (it emitted nothing) advances its step counter instead, so
   *  its next step still gets a distinct id. */
  endStep(thread: number, eventCount: number): void {
    const step = this.steps[this.currentStep];
    if (!step) return;
    step.endEvent = eventCount;
    const last = this.lastEvent.get(thread);
    if (last === undefined || last < step.firstEvent) {
      this.sinceEvent.set(thread, (this.sinceEvent.get(thread) ?? 0) + 1);
    }
    this.currentStep = -1;
  }

  /** Record an event and derive its id from its causal predecessors. */
  recordEvent(index: number, ev: TraceEvent, causes?: ExtraCauses): void {
    const tid = eventThread(ev);
    const deps: number[] = [];

    // Program order — except a thread's first event, which is caused by the
    // spawn that created it (already this thread's chain head, and `lastEvent`
    // has no entry for it).
    const prev = this.lastEvent.get(tid);
    if (prev !== undefined) deps.push(prev);
    else if (ev.type === "thread-start") {
      const origin = this.spawnOf.get(tid);
      if (origin !== undefined) deps.push(origin);
    }

    // Reads-from: the event that published the version this read observed.
    if (ev.type === "read") {
      const from = this.author.get(`${ev.addr}@${ev.version}`);
      if (from !== undefined && from !== prev) deps.push(from);
    }

    // A retry-park wake: the commit that woke this thread caused its re-attempt.
    const woke = this.wokenBy.get(tid);
    if (woke !== undefined) {
      this.wokenBy.delete(tid);
      if (woke !== prev) deps.push(woke);
    }

    for (const e of causes?.extra ?? []) if (!deps.includes(e)) deps.push(e);

    const id = hashParts([eventPayload(ev), ...deps.map((d) => this.eventIds[d] ?? "?")]);
    this.eventIds[index] = id;
    this.deps[index] = deps;
    this.eventStep[index] = this.currentStep;

    this.lastEvent.set(tid, index);
    this.chainHead.set(tid, id);
    this.sinceEvent.set(tid, 0);

    // A spawn is the child's origin: it stands in as the child's chain head
    // until the child emits its own first event, so the child's very first step
    // already has a stable id.
    // A spawn is the child's origin: it stands in as the child's chain head
    // until the child emits its own first event, so the child's very first step
    // already has a stable id. Tagged, or the child's first step and the
    // parent's next one would hash identically — both would be "the step after
    // the spawn, with nothing emitted since".
    if (ev.type === "spawn") {
      this.spawnOf.set(ev.child, index);
      this.chainHead.set(ev.child, hashParts([id, "spawned"]));
      this.sinceEvent.set(ev.child, 0);
    }

    // `wakeRetryParked` runs immediately before the commit that did the waking is
    // emitted, so this is that commit.
    if (ev.type === "txn-commit" && this.pendingWakes.length > 0) {
      for (const t of this.pendingWakes) this.wokenBy.set(t, index);
      this.pendingWakes = [];
    }
  }

  /** Every address written at `version` was published by event `index`. */
  noteCommit(addrs: Iterable<string>, version: number, index: number): void {
    for (const addr of addrs) this.author.set(`${addr}@${version}`, index);
  }

  /** The event that published `addr@version`, if any — the interpreter's lookup
   *  for the validation edge (which commit doomed this transaction). */
  publisher(addr: string, version: number): number | undefined {
    return this.author.get(`${addr}@${version}`);
  }

  /** A thread was woken out of a `retry` park by the commit about to be emitted. */
  noteRetryWake(thread: number): void {
    this.pendingWakes.push(thread);
  }
}
